import { Button, Radio, Tooltip, Typography, toast } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { MfaStatus } from '@nx/state';
import {
  MFA_STATUS_REASON,
  addNotification,
  consoleApi,
  getApiError,
  getErrorMessage,
  logout,
  useCapability,
  useSetting,
} from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { ControlPanel, ControlPanelNew } from '@nx/ui/src/control-panel';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useState } from 'react';

import { ConfirmMfaModal } from './confirm-mfa-modal';
import DarkThemeSvg from './dark-theme.svg';
import LightThemeSvg from './light-theme.svg';
import SystemThemeSvg from './system-theme.svg';

const logger = createLogger(APP_SCOPE.framework);

const themeOptions = [
  { label: 'Light', value: 'light' as const },
  { label: 'Dark', value: 'dark' as const },
  { label: 'System default', value: 'system' as const },
];

type ThemeOptions = (typeof themeOptions)[number]['value'];

export const mfaTooltipContent = (mfaStatus: MfaStatus) => {
  switch (mfaStatus.reason) {
    case MFA_STATUS_REASON.SSO_APPLIED:
      return 'MFA is required through your SSO Identity Provider (IdP).';
    case MFA_STATUS_REASON.EXTERNAL_AUTH_PROVIDER:
      return 'Enable MFA via your external authentication provider.';
    case MFA_STATUS_REASON.ORG_REQUIRED:
      return 'MFA is mandated by your organization.';
    case MFA_STATUS_REASON.NOT_ENROLLED:
      return 'You have not yet completed your MFA setup.';
    default:
      return `You can not ${mfaStatus.enabled ? 'disable' : 'enable'} MFA.`;
  }
};

const ThemeCard = ({
  Svg,
  theme,
  onClick,
  isSelected,
}: {
  Svg: string;
  theme: ThemeOptions;
  onClick: (theme: ThemeOptions) => void;
  isSelected: boolean;
}) => {
  return (
    <div
      className="border-palette-neutral-border-weak focus-visible:outline-palette-primary-focus flex max-w-[260px] flex-col gap-3 rounded-lg border p-3 hover:cursor-pointer focus:outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
      onClick={() => {
        onClick(theme);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(theme);
        }
      }}
      onTouchStart={(e) => {
        e.preventDefault();
        onClick(theme);
      }}
      tabIndex={0}
    >
      <img src={Svg} alt="Theme preview" />
      <Radio
        className="w-fit"
        value={theme}
        label={theme.charAt(0).toUpperCase() + theme.slice(1)}
        isChecked={isSelected}
        isReadOnly={true}
        htmlAttributes={{
          'aria-hidden': true,
          tabIndex: -1,
        }}
      />
    </div>
  );
};

const PreferencesLayout = () => {
  const [isThemeSwitcherOn] = useCapability('framework:theme-switcher');
  const [theme, setTheme] = useSetting('global', 'theme');
  const [showModal, setShowModal] = useState(false);

  const [updateUserMfaEnabled, updateUserMfaEnabledRes] = consoleApi.useUpdateUserPreferenceMfaMutation();

  const { data: user } = consoleApi.useGetUserDetailsQuery();

  const handleUpdateMfaEnabled = () => {
    if (isNullish(user)) {
      return;
    }

    const currentlyEnabled = user.mfaStatus.enabled;
    updateUserMfaEnabled({ userId: user.id, mfaEnabled: !currentlyEnabled })
      .unwrap()
      .then(() => {
        setShowModal(false);
        if (!currentlyEnabled) {
          logout();
        } else {
          toast.success('Successfully disabled MFA.', { shouldAutoClose: true });
        }
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          logger.error(error.message);
        }
        addNotification({
          type: 'danger',
          title: `Failed to ${currentlyEnabled ? 'disable' : 'enable'} MFA`,
          description: getErrorMessage(error),
          timeout: 5000,
        });
      });
  };

  const onToggleClick = () => {
    setShowModal(true);
  };

  const mfaButtonDisabled =
    isNotNullish(user) &&
    [
      MFA_STATUS_REASON.ORG_REQUIRED,
      MFA_STATUS_REASON.EXTERNAL_AUTH_PROVIDER,
      MFA_STATUS_REASON.NOT_ENROLLED,
      MFA_STATUS_REASON.SSO_APPLIED,
    ].includes(user.mfaStatus.reason);

  return (
    <>
      {isThemeSwitcherOn && (
        <ControlPanelNew>
          <ControlPanelNew.Content
            title={
              <div className="flex flex-col items-start gap-1">
                <Typography variant="h6">Theme</Typography>
                <Typography variant="body-medium">Choose how you want Aura to look to you.</Typography>
              </div>
            }
          >
            <div className="flex flex-wrap gap-4">
              <ThemeCard Svg={LightThemeSvg} theme="light" onClick={setTheme} isSelected={theme === 'light'} />
              <ThemeCard Svg={DarkThemeSvg} theme="dark" onClick={setTheme} isSelected={theme === 'dark'} />
              <ThemeCard Svg={SystemThemeSvg} theme="system" onClick={setTheme} isSelected={theme === 'system'} />
            </div>
          </ControlPanelNew.Content>
        </ControlPanelNew>
      )}
      {user?.mfaStatus !== undefined && user.mfaStatus.reason !== MFA_STATUS_REASON.NON_APPLICABLE && (
        <ControlPanelNew>
          <ControlPanelNew.Content title="Security">
            <ControlPanel.Row
              title="App MFA (Multi-Factor Authentication)"
              description="Turn on MFA"
              action={
                <Tooltip type="simple" isDisabled={!mfaButtonDisabled}>
                  <Tooltip.Trigger hasButtonWrapper>
                    <Button
                      size="small"
                      color={user.mfaStatus.enabled ? 'danger' : 'neutral'}
                      fill="outlined"
                      onClick={onToggleClick}
                      aria-label={`${user.mfaStatus.enabled ? 'Disable' : 'Enable'} MFA`}
                      isDisabled={mfaButtonDisabled}
                    >
                      {user.mfaStatus.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content style={{ maxWidth: 200 }}>{mfaTooltipContent(user.mfaStatus)}</Tooltip.Content>
                </Tooltip>
              }
            />
          </ControlPanelNew.Content>
        </ControlPanelNew>
      )}
      {showModal && user?.mfaStatus !== undefined && (
        <ConfirmMfaModal
          onClose={() => setShowModal(false)}
          onConfirm={handleUpdateMfaEnabled}
          isLoading={updateUserMfaEnabledRes.isLoading}
          title={`${user.mfaStatus.enabled ? 'Disable' : 'Enable'} Multi Factor Authentication (MFA)?`}
          mfaEnabled={user.mfaStatus.enabled}
          description="Note: Confirming will log you out and redirect you to the login page to set up MFA."
        />
      )}
    </>
  );
};

export { PreferencesLayout, PreferencesLayout as PreferencesCard };
